import type {
  DuesRepository,
  DuesRosterInput,
  RecordDuePaymentInput,
  SaveDuesSettingsInput,
  SetDueExemptionInput,
  StartDueOnlinePaymentInput,
  TenantContext,
} from '@zetyar/data-contracts'
import type {
  DuePaymentMethod,
  DuesPeriodSummary,
  DuesRosterEntry,
  MembershipDue,
  MembershipDueStatus,
  OrgDuesSettings,
  UUID,
} from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

// Mirrors app.release_stale_due_holds (0033): an abandoned online checkout stops holding its months.
const PROCESSING_HOLD_MS = 2 * 60 * 60 * 1000

interface SettingsRow {
  readonly organization_id: string
  readonly monthly_amount: number
  readonly currency: string
  readonly due_day: number
  readonly bank_name: string | null
  readonly account_type: string | null
  readonly account_number: string | null
  readonly account_holder: string | null
  readonly account_rut: string | null
  readonly account_email: string | null
  readonly instructions: string | null
  readonly online_payment_enabled: boolean
  readonly updated_at: string
}

const SETTINGS_SELECT =
  'organization_id, monthly_amount, currency, due_day, bank_name, account_type, account_number, account_holder, account_rut, account_email, instructions, online_payment_enabled, updated_at'

function mapSettings(row: SettingsRow): OrgDuesSettings {
  return {
    organizationId: row.organization_id,
    monthlyAmount: Number(row.monthly_amount),
    currency: row.currency,
    dueDay: row.due_day,
    bankName: row.bank_name,
    accountType: row.account_type,
    accountNumber: row.account_number,
    accountHolder: row.account_holder,
    accountRut: row.account_rut,
    accountEmail: row.account_email,
    instructions: row.instructions,
    onlinePaymentEnabled: row.online_payment_enabled,
    updatedAt: row.updated_at,
  }
}

interface DueRow {
  readonly id: string
  readonly membership_id: string
  readonly period: string
  readonly status: MembershipDueStatus
  readonly amount: number
  readonly currency: string
  readonly due_payment_id: string | null
  readonly notes: string | null
  readonly created_at: string
  readonly due_payment: { readonly method: DuePaymentMethod; readonly paid_at: string | null } | null
}

const DUE_SELECT =
  'id, membership_id, period, status, amount, currency, due_payment_id, notes, created_at, due_payment:due_payments (method, paid_at)'

function isExpiredHold(status: MembershipDueStatus, createdAt: string): boolean {
  return status === 'processing' && Date.now() - new Date(createdAt).getTime() > PROCESSING_HOLD_MS
}

function mapDue(row: DueRow): MembershipDue {
  return {
    id: row.id,
    membershipId: row.membership_id,
    period: row.period,
    status: row.status,
    amount: Number(row.amount),
    currency: row.currency,
    duePaymentId: row.due_payment_id,
    paymentMethod: row.due_payment?.method ?? null,
    paidAt: row.due_payment?.paid_at ?? null,
    notes: row.notes,
  }
}

interface RosterRow {
  readonly membership_id: string
  readonly display_name: string | null
  readonly member_number: string | null
  readonly member_position: string | null
  readonly institutional_email: string | null
  readonly due_id: string | null
  readonly due_status: MembershipDueStatus | null
  readonly due_payment_id: string | null
  readonly payment_method: DuePaymentMethod | null
  readonly paid_at: string | null
  readonly due_amount: number | null
  readonly total_count: number
}

function mapRosterRow(row: RosterRow): DuesRosterEntry {
  return {
    membershipId: row.membership_id,
    displayName: row.display_name ?? 'Socio',
    memberNumber: row.member_number,
    position: row.member_position,
    institutionalEmail: row.institutional_email,
    dueId: row.due_id,
    dueStatus: row.due_status,
    duePaymentId: row.due_payment_id,
    paymentMethod: row.payment_method,
    paidAt: row.paid_at,
    amount: row.due_amount === null ? null : Number(row.due_amount),
  }
}

async function readFunctionErrorCode(error: unknown): Promise<string | null> {
  const response = (error as { context?: Response } | null)?.context
  if (!response || typeof response.json !== 'function') return null
  try {
    const body = (await response.json()) as { error?: string }
    return body.error ?? null
  } catch {
    return null
  }
}

export function createSupabaseDuesRepository(client: SupabaseClient): DuesRepository {
  return {
    async getSettings(context: TenantContext) {
      const { data, error } = await client
        .from('organization_dues_settings')
        .select(SETTINGS_SELECT)
        .eq('organization_id', context.organizationId)
        .maybeSingle()

      if (error) throw error
      return data ? mapSettings(data as SettingsRow) : null
    },

    async listMemberDues(context: TenantContext, membershipId: UUID, { limit }) {
      const { data, error } = await client
        .from('membership_dues')
        .select(DUE_SELECT)
        .eq('organization_id', context.organizationId)
        .eq('membership_id', membershipId)
        .is('voided_at', null)
        .order('period', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 120)))

      if (error) throw error
      return (data as unknown as DueRow[]).filter((row) => !isExpiredHold(row.status, row.created_at)).map(mapDue)
    },

    async getPeriodSummary(context: TenantContext, period) {
      const { data, error } = await client
        .rpc('get_dues_period_summary', { p_organization_id: context.organizationId, p_period: period })
        .single()

      if (error) throw error
      const row = data as {
        active_members: number
        paid_count: number
        exempt_count: number
        processing_count: number
        collected_amount: number
      }
      const activeMembers = Number(row.active_members)
      const paidCount = Number(row.paid_count)
      const exemptCount = Number(row.exempt_count)
      const summary: DuesPeriodSummary = {
        activeMembers,
        paidCount,
        exemptCount,
        processingCount: Number(row.processing_count),
        pendingCount: Math.max(0, activeMembers - paidCount - exemptCount),
        collectedAmount: Number(row.collected_amount),
      }
      return summary
    },

    async saveSettings(context: TenantContext, input: SaveDuesSettingsInput) {
      const { data, error } = await client
        .rpc('save_dues_settings', {
          p_organization_id: context.organizationId,
          p_monthly_amount: Math.round(input.monthlyAmount),
          p_currency: input.currency,
          p_due_day: input.dueDay,
          p_bank_name: input.bankName ?? null,
          p_account_type: input.accountType ?? null,
          p_account_number: input.accountNumber ?? null,
          p_account_holder: input.accountHolder ?? null,
          p_account_rut: input.accountRut ?? null,
          p_account_email: input.accountEmail ?? null,
          p_instructions: input.instructions ?? null,
          p_online_payment_enabled: input.onlinePaymentEnabled,
        })
        .single()

      if (error) throw error
      return mapSettings(data as SettingsRow)
    },

    async listRoster(context: TenantContext, input: DuesRosterInput) {
      const { data, error } = await client.rpc('list_dues_roster', {
        p_organization_id: context.organizationId,
        p_period: input.period,
        p_search: input.search?.trim() || null,
        p_status: input.status ?? null,
        p_limit: input.limit,
        p_offset: input.offset,
      })

      if (error) throw error
      const rows = (data ?? []) as RosterRow[]
      return { entries: rows.map(mapRosterRow), total: Number(rows[0]?.total_count ?? 0) }
    },

    async recordPayment(context: TenantContext, input: RecordDuePaymentInput) {
      const { data, error } = await client.rpc('record_due_payment', {
        p_organization_id: context.organizationId,
        p_membership_id: input.membershipId,
        p_start_period: input.startPeriod,
        p_months: input.months,
        p_method: input.method,
        p_notes: input.notes?.trim() || null,
      })

      if (error) throw error
      return data as UUID
    },

    async revertPayment(context: TenantContext, duePaymentId: UUID, reason?: string) {
      const { error } = await client.rpc('revert_due_payment', {
        p_organization_id: context.organizationId,
        p_due_payment_id: duePaymentId,
        p_reason: reason?.trim() || null,
      })
      if (error) throw error
    },

    async setExemption(context: TenantContext, input: SetDueExemptionInput) {
      const { error } = await client.rpc('set_due_exemption', {
        p_organization_id: context.organizationId,
        p_membership_id: input.membershipId,
        p_period: input.period,
        p_exempt: input.exempt,
        p_notes: input.notes?.trim() || null,
      })
      if (error) throw error
    },

    async startOnlinePayment(context: TenantContext, input: StartDueOnlinePaymentInput) {
      if (!context.membershipId) throw new Error('dues_forbidden')

      const { data, error } = await client.functions.invoke('create-due-preference', {
        body: {
          organizationId: context.organizationId,
          startPeriod: input.startPeriod,
          months: input.months,
        },
      })
      if (error) {
        throw new Error((await readFunctionErrorCode(error)) ?? 'dues_online_payment_failed')
      }

      const initPoint = (data as { initPoint?: string } | null)?.initPoint
      if (!initPoint) throw new Error('dues_online_payment_failed')
      return { initPoint }
    },
  }
}
